import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { getUtcDisplayDate } from "encamp-shared/src/utils/date";
import {
  isGeneratedDocumentNumber,
  manifestNumberLabel,
} from "encamp-shared/src/waste/shippingDocumentLabels";
import { WasteManifestSource } from "generated-graphql/graphql";
import { ReactNode } from "react";

import { billOfLadingDrawerSections } from "./billOfLadingDrawerSections";
import { CorrectionStatusChip } from "./CorrectionStatusChip";
import { ManifestCommentsPopover } from "./ManifestCommentsPopover";
import { pickByPriority } from "./manifestSourceDisplay";
import { ManifestDetail } from "./sections/types";

interface StickyManifestHeaderProps {
  manifest: ManifestDetail;
  isStaff: boolean;
  /** Whether this manifest (or any of its lines) was manually edited. */
  edited: boolean;
  onEdit: () => void;
  /** Edits an uploaded bill of lading; set only when the viewer may. */
  onEditBill?: () => void;
  onClose: () => void;
  actions?: ReactNode;
}

/**
 * Sticky drawer header: tracking #, a couple of at-a-glance facts (ship date,
 * TSDF), and the edit/document/close actions. Stays pinned while the body
 * scrolls. Source selection now lives in the body's "Data source" tabs, so the
 * header carries no source affordance.
 */
export function StickyManifestHeader({
  manifest,
  isStaff,
  edited,
  onEdit,
  onEditBill,
  onClose,
  actions,
}: StickyManifestHeaderProps) {
  const trackingNumber =
    manifest.vendorManifest?.manifestTrackingNumber ?? manifest.manifestTrackingNumber;
  const sections = billOfLadingDrawerSections(manifest.documentKind);
  const billNumberText = isGeneratedDocumentNumber(trackingNumber)
    ? manifestNumberLabel({
        documentKind: manifest.documentKind ?? "",
        manifestTrackingNumber: trackingNumber,
      })
    : `${sections.numberLabel} ${trackingNumber}`;
  const available = manifest.availableSources;
  const primary = manifest.primarySource;
  const shippedDate = pickByPriority(primary, available, {
    [WasteManifestSource.EManifest]: manifest.eManifest?.shippedDate,
    [WasteManifestSource.Vendor]: manifest.vendorManifest?.shippedDate,
    [WasteManifestSource.ManifestDocument]: manifest.documentManifest?.shippedDate,
  });
  const tsdf = pickByPriority(primary, available, {
    [WasteManifestSource.EManifest]: manifest.eManifest?.desFacilityName,
    [WasteManifestSource.Vendor]: manifest.vendorManifest?.desFacilityName,
    [WasteManifestSource.ManifestDocument]: manifest.documentManifest?.desFacilityName,
  });

  return (
    <Box
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 2,
        bgcolor: "background.paper",
        borderBottom: 1,
        borderColor: "divider",
        px: 3,
        py: 2,
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
        <Stack spacing={0.5} minWidth={0}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography variant="h5">
              {sections.isBillOfLading ? sections.paperName : `Manifest #${trackingNumber}`}
            </Typography>
            {(onEditBill || (isStaff && manifest.vendorManifest)) && (
              <>
                {/* Edit affordance moved next to the number, icon-only. */}
                <Tooltip title={sections.editDataLabel}>
                  <IconButton
                    size="small"
                    color={onEditBill ? "primary" : "staff"}
                    onClick={onEditBill ?? onEdit}
                    aria-label={sections.editDataLabel}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {edited && (
                  <Typography variant="caption" color="textDisabled" fontStyle="italic">
                    edited
                  </Typography>
                )}
              </>
            )}
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            {sections.isBillOfLading && (
              <Typography variant="body2" color="text.secondary">
                {billNumberText}
              </Typography>
            )}
            {shippedDate && (
              <Typography variant="body2" color="text.secondary">
                Shipped {getUtcDisplayDate(shippedDate)}
              </Typography>
            )}
            {tsdf && (
              <Typography variant="body2" color="text.secondary" noWrap>
                · {tsdf}
              </Typography>
            )}
          </Stack>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
          {actions}
          <CorrectionStatusChip status={manifest.latestEpaCorrectionManifestStatus} />
          {isStaff && <ManifestCommentsPopover processedManifestId={manifest.id} />}
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </Stack>
    </Box>
  );
}
